import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { Toaster } from 'sonner';

import { CookieBanner } from '@/components/cookie-banner';
import { InstallAppBanner } from '@/components/install-app-banner';
import { AppShell } from '@/components/layout/app-shell';
import { SkipToMain } from '@/components/skip-to-main';
import { AbastecerPage } from '@/features/abastecer/abastecer-page';
import { AssinaturaPage } from '@/features/assinatura/assinatura-page';
import { PlanosPage } from '@/features/assinatura/planos-page';
import { LoginPage } from '@/features/auth/login-page';
import { RecuperarSenhaPage } from '@/features/auth/recuperar-senha-page';
import { RedefinirSenhaPage } from '@/features/auth/redefinir-senha-page';
import { SignupPage } from '@/features/auth/signup-page';
import { ComprarAvulsoPage } from '@/features/avulso/comprar-avulso-page';
import { AceitarConvitePage } from '@/features/convite/aceitar-convite-page';
import { FidelidadePage } from '@/features/fidelidade/fidelidade-page';
import { GerarCodigoPage } from '@/features/home/gerar-codigo-page';
import { HomePage } from '@/features/home/home-page';
import { HomePageV2 } from '@/features/home/home-page-v2';
import { LavagemDetalhePage } from '@/features/lavagens/lavagem-detalhe-page';
import { LavagensPage } from '@/features/lavagens/lavagens-page';
import { LgpdPage } from '@/features/lgpd/lgpd-page';
import { NotFoundPage } from '@/features/not-found/not-found-page';
import { NotificacoesPage } from '@/features/notificacoes/notificacoes-page';
import { OnboardingPage } from '@/features/onboarding/onboarding-page';
import { PagamentosPage } from '@/features/pagamentos/pagamentos-page';
import { ValidarResgateParceiroPage } from '@/features/parceiro/validar-resgate-page';
import { DispositivosPage } from '@/features/perfil/dispositivos-page';
import { EditarDadosPage } from '@/features/perfil/editar-dados-page';
import { PerfilPage } from '@/features/perfil/perfil-page';
import { SubprocessadoresPage } from '@/features/public/subprocessadores-page';
import { MeusCuponsPage, ResgatarPage } from '@/features/resgatar/resgatar-page';
import { MapaPage } from '@/features/unidades/mapa-page';
import { AdicionarVeiculoPage } from '@/features/veiculos/adicionar-veiculo-page';
import { DetalheVeiculoPage } from '@/features/veiculos/detalhe-veiculo-page';
import { EditarVeiculoPage } from '@/features/veiculos/editar-veiculo-page';
import { VeiculosPage } from '@/features/veiculos/veiculos-page';
import { AuthProvider, RequireCliente } from '@/lib/auth-context';
import { TenantProvider } from '@/lib/tenant-context';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: { retry: 1, staleTime: 30_000, refetchOnWindowFocus: false },
  },
});

export function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <AuthProvider>
          <BrowserRouter>
            <SkipToMain />
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/cadastro" element={<SignupPage />} />
              <Route path="/recuperar-senha" element={<RecuperarSenhaPage />} />
              <Route path="/esqueci-senha" element={<RecuperarSenhaPage />} />
              <Route path="/redefinir-senha" element={<RedefinirSenhaPage />} />
              <Route path="/p/validar" element={<ValidarResgateParceiroPage />} />
              <Route path="/convite/:token" element={<AceitarConvitePage />} />
              <Route path="/subprocessadores" element={<SubprocessadoresPage />} />
              {/* Onboarding precisa de auth, mas roda fora do AppShell pra ter tela cheia */}
              <Route
                path="/onboarding"
                element={
                  <RequireCliente>
                    <OnboardingPage />
                  </RequireCliente>
                }
              />

              <Route
                element={
                  <RequireCliente>
                    <AppShell />
                  </RequireCliente>
                }
              >
                <Route path="/" element={<HomePage />} />
                {/* V2 com refinamento Impeccable (teste — apaga depois) */}
                <Route path="/v2" element={<HomePageV2 />} />
                <Route path="/veiculos" element={<VeiculosPage />} />
                <Route path="/veiculos/adicionar" element={<AdicionarVeiculoPage />} />
                <Route path="/veiculos/:seqVeiculo" element={<DetalheVeiculoPage />} />
                <Route path="/veiculos/:seqVeiculo/editar" element={<EditarVeiculoPage />} />
                <Route path="/assinatura" element={<AssinaturaPage />} />
                <Route path="/planos" element={<PlanosPage />} />
                <Route path="/pagamentos" element={<PagamentosPage />} />
                <Route path="/mapa" element={<MapaPage />} />
                <Route path="/gerar-codigo" element={<GerarCodigoPage />} />
                <Route path="/abastecer" element={<AbastecerPage />} />
                <Route path="/resgatar" element={<ResgatarPage />} />
                <Route path="/meus-cupons" element={<MeusCuponsPage />} />
                <Route path="/avulso/comprar" element={<ComprarAvulsoPage />} />
                <Route path="/lavagens" element={<LavagensPage />} />
                <Route path="/lavagens/:id" element={<LavagemDetalhePage />} />
                <Route path="/lavagem/:id" element={<LavagemDetalhePage />} />
                <Route path="/fidelidade" element={<FidelidadePage />} />
                <Route path="/perfil" element={<PerfilPage />} />
                <Route path="/perfil/editar" element={<EditarDadosPage />} />
                <Route path="/perfil/dispositivos" element={<DispositivosPage />} />
                <Route path="/lgpd" element={<LgpdPage />} />
                <Route path="/notificacoes" element={<NotificacoesPage />} />
              </Route>

              {/* Catch-all 404 — tela premium com personalidade (não redireciona pra /) */}
              <Route path="*" element={<NotFoundPage />} />
            </Routes>
            {/* CookieBanner e InstallAppBanner usam <Link> do react-router-dom,
                entao precisam estar DENTRO do BrowserRouter senao quebram com
                "Cannot destructure property 'basename' of useContext(...)". */}
            <CookieBanner />
            <InstallAppBanner />
          </BrowserRouter>
          <Toaster position="top-right" richColors />
        </AuthProvider>
      </TenantProvider>
    </QueryClientProvider>
  );
}
